import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { loadMessages, routing, type Locale } from "@qrody/i18n";

/**
 * Minimal i18n provider that mirrors next-intl's `useTranslations`
 * contract for the Vite SPA. We keep the API surface small on purpose
 * — the JSON message bundles live in @qrody/i18n and are shared with
 * the marketing Next app, so any new namespace lands in one place.
 *
 * `useTranslations(namespace)` returns a function `t(key)` that looks
 * up `messages[namespace][key]`, mirroring next-intl's behaviour
 * closely enough that the migrated pages can import this module
 * instead and not have to relearn an API.
 *
 * If a key is missing we surface a `[ns.key]` placeholder rather than
 * the silent empty string next-intl prints in prod. The migration
 * smoke-test catches missing keys faster that way.
 */
type Messages = Record<string, unknown>;

type IntlContextValue = {
  locale: Locale;
  messages: Messages;
  setLocale: (next: Locale) => void;
};

const IntlContext = createContext<IntlContextValue | null>(null);

export function IntlProvider({
  initialLocale,
  children,
}: {
  initialLocale?: Locale;
  children: ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? routing.defaultLocale);
  const [messages, setMessages] = useState<Messages>({});

  useEffect(() => {
    let cancelled = false;
    loadMessages(locale, "app").then((next) => {
      if (!cancelled) setMessages(next);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<IntlContextValue>(
    () => ({ locale, messages, setLocale: setLocaleState }),
    [locale, messages],
  );

  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
}

export function useLocale(): Locale {
  const ctx = useContext(IntlContext);
  if (!ctx) throw new Error("useLocale must be used inside <IntlProvider>");
  return ctx.locale;
}

type Primitive = string | number | boolean;
type Vars = Record<string, Primitive>;

/**
 * Returns a translator bound to `namespace`. Mirrors next-intl's
 * `t(key, vars?)` contract — ICU-style `{name}` placeholders in the
 * template are substituted with the matching `vars` entry. The
 * `@qrody/qr-generator` package is the heaviest consumer (e.g.
 * `t("error.validationFailed", { detail })`), so without `{var}`
 * support the user-visible error strings would render the raw
 * placeholder.
 */
export function useTranslations(namespace?: string) {
  const ctx = useContext(IntlContext);
  if (!ctx) throw new Error("useTranslations must be used inside <IntlProvider>");
  const { messages } = ctx;

  // The returned translator was a fresh closure on every render before —
  // safe to call but a reference-identity hazard. Consumers like
  // CodeSidebarPanel feed `t` into a useMemo that controls a sidebar
  // slot via useSetAppSidebarSlot; with `t` changing every render the
  // memo invalidated every render, the slot setNode fired every render,
  // the provider re-rendered every render, → "Maximum update depth
  // exceeded". Memoising the translator by `(messages, namespace)`
  // gives downstream memos a stable dependency so the loop closes.
  return useCallback(
    (key: string, vars?: Vars): string => {
      const fullPath = namespace ? `${namespace}.${key}` : key;
      const value = resolvePath(messages, fullPath);
      if (typeof value === "string") return interpolate(value, vars);
      if (typeof value === "number" || typeof value === "boolean") return String(value);
      return `[${fullPath}]`;
    },
    [messages, namespace],
  );
}

function interpolate(template: string, vars: Vars | undefined): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const replacement = vars[name];
    return replacement === undefined ? match : String(replacement);
  });
}

function resolvePath(root: Messages, path: string): unknown {
  let cursor: unknown = root;
  for (const segment of path.split(".")) {
    if (cursor && typeof cursor === "object" && segment in (cursor as Record<string, unknown>)) {
      cursor = (cursor as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return cursor;
}
